import { useMemo, useContext } from 'react';
import { TableContext } from '../../../components/shared/table/context/TableContext';
import { formatFileSize } from '../../../utils/fileUtils';
import { useAuth } from '../../../context/AuthContext';
import { formatDate } from '../../../utils/dateUtils';

export const useColumns = (documents = []) => {
  const { user } = useAuth();
  const { preferences: { columnOrder = [] } } = useContext(TableContext);

  return useMemo(() => {
    if (!documents.length) return [];

    // Define base columns with appropriate widths
    const columns = [
      {
        field: 'title',
        headerName: 'Name',
        type: 'string',
        width: 250, // Wider for document names
        sortable: true,
        filterOperators: [
          { value: 'contains', label: 'Contains' },
          { value: 'equals', label: 'Equals' },
          { value: 'startsWith', label: 'Starts with' },
          { value: 'endsWith', label: 'Ends with' }
        ],
        renderCell: (document) => document.title
      },
      {
        field: 'type_name',
        headerName: 'Type',
        type: 'string',
        width: 120, // Narrower for document types
        sortable: true,
        filterOperators: [
          { value: 'equals', label: 'Equals' },
          { value: 'notEquals', label: 'Not equals' }
        ],
        renderCell: (document) => document.type_name
      },
      {
        field: 'size',
        headerName: 'Size',
        type: 'number',
        width: 100, // Narrow for file sizes
        sortable: true,
        filterOperators: [
          { value: '>', label: 'Greater than' },
          { value: '<', label: 'Less than' },
          { value: '>=', label: 'Greater than or equals' },
          { value: '<=', label: 'Less than or equals' }
        ],
        renderCell: (document) => document.file_size ? formatFileSize(document.file_size) : '0 B'
      },
      {
        field: 'created_at',
        headerName: 'Uploaded',
        type: 'date',
        width: 120, // Standard width for dates
        sortable: true,
        filterOperators: [
          { value: 'equals', label: 'Equals' },
          { value: '>', label: 'After' },
          { value: '<', label: 'Before' }
        ],
        renderCell: (document) => document.created_at ? formatDate(document.created_at, { user, includeTime: true }) : '-'
      },
      {
        field: 'notes',
        headerName: 'Notes',
        type: 'string',
        width: 200, // Wider for notes text
        sortable: true,
        filterOperators: [
          { value: 'contains', label: 'Contains' },
          { value: 'equals', label: 'Equals' },
          { value: 'startsWith', label: 'Starts with' },
          { value: 'endsWith', label: 'Ends with' }
        ],
        renderCell: (document) => (
          <div className="truncate" title={document.notes || '-'}>
            {document.notes || '-'}
          </div>
        )
      }
    ];

    // Apply column order if specified
    if (columnOrder.length > 0) {
      const orderMap = new Map(columnOrder.map((field, index) => [field, index]));
      columns.sort((a, b) => {
        const aIndex = orderMap.has(a.field) ? orderMap.get(a.field) : Number.MAX_SAFE_INTEGER;
        const bIndex = orderMap.has(b.field) ? orderMap.get(b.field) : Number.MAX_SAFE_INTEGER;
        return aIndex - bIndex;
      });
    }

    return columns;
  }, [documents.length, columnOrder]); // Recompute if documents length or column order changes
};
